import { Inject, Injectable } from '@angular/core';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { Division, Region } from './ebr-api-client/ebr-api-client.g';
import { getTemplatesAP, getTemplatesIMEA, getTemplatesLA, TEMPLATE_TRANSLATIONS, TEMPLATES, TEMPLATES_EU, TEMPLATES_NA } from 'src/app/features/filter-panel/types/filters';
import { FEATURE_FLAGS, FeatureFlags } from 'src/app/config/AppInitializer.factory';
import { PresentationType } from '../types/presentation-types';
import { FilterValue } from 'src/app/features/filter-panel/types/base';

@Injectable({
  providedIn: 'root'
})
export class TemplateTranslationsService {
  constructor(
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
  ) { }

  // TODO : Move away from Template name methods and use template codes for all logic as API workflows are now based on template code.
  getAvailableTranslationsByTemplate(template: string) {
    let translations: string[] = [];

    switch (template){
      case "Lodging":
        translations = ['English', 'Arabic', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'];
        break;
      case "Food Service":
        translations = ['English', 'Portuguese (Brazil)', 'French (EU)', 'German', 'Italian', 'Japanese', 'Korean', 'Spanish (EU)', 'Spanish (LA)', 'Turkish'];
        break;
      case "Facilities":
        translations = ['English', 'Arabic', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Spanish (LA)', 'Turkish']
        break;
      case "Education":
        translations = ['English', 'Arabic', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'];
        break;
      case "Long Term Care":
        translations = ['English', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'];
        break;
      case "SMARTPOWER":
        translations = ['English', 'Japanese'];
        break;
      case "TVD":
        translations = ['English'];
        break;
      case "Other":
        translations = ['English', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'];
        break;
      case "All":
        translations = ['English'];
        break;
      default:
        translations = ['English']
    }

    return translations;
  }

  /**
   * Get available translation FilterValues for a template code
   * @param templateCode code corresponding to selected template in the UI
   * @returns list of available translation names and codes for the given template
   */
  getAvailableTranslationsByTemplateCode(templateCode: PresentationType): FilterValue[] {

    let translationNames: string[] = [];

    switch (templateCode) {
      case PresentationType.Lodging:
        translationNames = [
          'English', 'Arabic', 'Portuguese (Brazil)',
          'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'
        ];
        break;

      case PresentationType.FoodService:
        translationNames = [
          'English', 'Portuguese (Brazil)', 'French (EU)',
          'German', 'Italian', 'Japanese', 'Korean',
          'Spanish (EU)', 'Spanish (LA)', 'Turkish'
        ];
        break;

      case PresentationType.Facilities:
      case PresentationType.Education:
        translationNames = [
          'English', 'Arabic', 'Portuguese (Brazil)',
          'Japanese', 'Korean', 'Spanish (LA)', 'Turkish'
        ];
        break;

      case PresentationType.LongTermCare:
      case PresentationType.Other:
        translationNames = [
          'English', 'Portuguese (Brazil)', 'Japanese',
          'Korean', 'Spanish (LA)', 'Turkish'
        ];
        break;

      case PresentationType.SMARTPOWER:
        translationNames = ['English', 'Japanese'];
        break;

      case PresentationType.TvdLA:
        translationNames = ['English', 'Spanish (LA)', 'Portuguese (Brazil)'];
        break;

      case PresentationType.TvdIMEA:
        translationNames = ['English', 'Turkish', 'French (EU)'];
        break;

      case PresentationType.TvdNA:
      case PresentationType.TvdEU:
      case PresentationType.TvdAP:
      case PresentationType.AllAP:
      default:
        translationNames = ['English'];
    }

    return translationNames
      .map(name => TEMPLATE_TRANSLATIONS.find(t => t.name === name))
      .filter((v): v is FilterValue => Boolean(v));
  }

  /**
   * Get available template FilterValues for a division
   * @param filters selected filters containing division and regions
   * @returns list of available templates for the given division
   */
  getAvailableTemplatesByDivision(filters:ContractModel): FilterValue[] {
    switch (filters.division) {
      case Division.Institutional:
        return this.getTemplatesInstitutional(filters.regions);

      // Currently only Institutional division allows for multiple template selection
      default:
        throw new Error(`Templates for division ${filters.division} are not defined`);
    }
  }

  /**
   * Get available template FilterValues for institutional division based on regions
   * @param regions list of selected regions for the institutional division
   * @returns list of available templates for the given regions
   */
  getTemplatesInstitutional(regions: string[] | undefined): FilterValue[] {
    let templates:{name: string, regionalLabel?: string, code: string}[] = [];

    regions?.forEach(region => {
      switch (region) {
        case Region.NA:
          templates = [...templates, ...TEMPLATES_NA];
          break;
        case Region.EU:
          templates = [...templates, ...TEMPLATES_EU];
          break;
        case Region.AP:
          templates = [...templates, ...getTemplatesAP(this._featureFlags.enableTVDForAPInstitutional)];
          break;
        case Region.LA:
          templates = [...templates, ...getTemplatesLA(this._featureFlags.enableTVDForLAInstitutional)];
          break;
        case Region.IMEA:
          templates = [...templates, ...getTemplatesIMEA(this._featureFlags.enableTVDAndSPForIMEAInstitutional)];
          break;
        default:
          templates = [...templates, ...TEMPLATES];
      }
    });

    return templates;
  }
}
